import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/services/api';
import PageShell from '../../../Components/PageShell';
import AdminNav from '../AdminNav';
import { STATUS_META, RECIPIENT_STATUS_META, campaignTotals, openRate } from './campaignMeta';
import './CampaignEditPage.css';

const DEFAULT_AUDIENCE = { exclude_organization_ids: [], admins_only: false };

const errMsg = (e) => e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Unknown error';

// ---- Audience -------------------------------------------------------------

// Filters + a live match count. The count comes from the read-only
// /audience/preview endpoint (never writes recipient rows) and is debounced so
// typing a date doesn't fire a request per keystroke.
const AudienceSection = ({ audience, onChange, disabled }) => {
    const [orgs, setOrgs] = useState([]);
    const [preview, setPreview] = useState(null);
    const [previewError, setPreviewError] = useState(null);

    useEffect(() => {
        api.get('admin/organisations')
            .then(setOrgs)
            .catch(() => setOrgs([]));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setPreviewError(null);
        const timer = setTimeout(() => {
            const params = new URLSearchParams();
            if (audience.signed_up_after) params.set('signed_up_after', audience.signed_up_after);
            if (audience.signed_up_before) params.set('signed_up_before', audience.signed_up_before);
            if (audience.exclude_organization_ids?.length) {
                params.set('exclude_organization_ids', audience.exclude_organization_ids.join(','));
            }
            if (audience.admins_only) params.set('admins_only', 'true');
            api.get(`admin/email/audience/preview?${params.toString()}`)
                .then((r) => { if (!cancelled) setPreview(r); })
                .catch((e) => { if (!cancelled) setPreviewError(errMsg(e)); });
        }, 400);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [audience]);

    const set = (patch) => onChange({ ...audience, ...patch });

    const excluded = audience.exclude_organization_ids || [];
    const orgName = (id) => orgs.find((o) => o.id === id)?.name || id;
    const addExclusion = (id) => {
        if (id && !excluded.includes(id)) set({ exclude_organization_ids: [...excluded, id] });
    };
    const removeExclusion = (id) => set({ exclude_organization_ids: excluded.filter((v) => v !== id) });

    return (
        <section className="campaign-card">
            <h2 className="campaign-card-title">Audience</h2>
            <p className="campaign-hint">
                All Live users match by default. Suppressed addresses (unsubscribes, bounces,
                spam reports) are always excluded — that rail is server-side and not optional.
            </p>
            <div className="campaign-audience-grid">
                <label className="campaign-field">
                    <span className="campaign-label">Signed up after</span>
                    <input
                        type="date"
                        className="campaign-input"
                        value={audience.signed_up_after || ''}
                        disabled={disabled}
                        onChange={(e) => set({ signed_up_after: e.target.value || undefined })}
                    />
                </label>
                <label className="campaign-field">
                    <span className="campaign-label">Signed up before</span>
                    <input
                        type="date"
                        className="campaign-input"
                        value={audience.signed_up_before || ''}
                        disabled={disabled}
                        onChange={(e) => set({ signed_up_before: e.target.value || undefined })}
                    />
                </label>
                <label className="campaign-field campaign-check">
                    <input
                        type="checkbox"
                        checked={!!audience.admins_only}
                        disabled={disabled}
                        onChange={(e) => set({ admins_only: e.target.checked })}
                    />
                    <span className="campaign-label">Admins only (safe rehearsal audience)</span>
                </label>
            </div>

            <div className="campaign-field">
                <span className="campaign-label">Exclude organisations</span>
                <select
                    className="campaign-input campaign-org-select"
                    value=""
                    disabled={disabled}
                    onChange={(e) => addExclusion(e.target.value)}
                >
                    <option value="">Add an organisation to exclude…</option>
                    {orgs.filter((o) => !excluded.includes(o.id)).map((o) => (
                        <option key={o.id} value={o.id}>{o.name}</option>
                    ))}
                </select>
                {excluded.length > 0 && (
                    <div className="campaign-chips">
                        {excluded.map((id) => (
                            <span key={id} className="campaign-chip">
                                {orgName(id)}
                                {!disabled && (
                                    <button type="button" onClick={() => removeExclusion(id)} aria-label={`Remove ${orgName(id)}`}>×</button>
                                )}
                            </span>
                        ))}
                    </div>
                )}
            </div>

            <div className="campaign-audience-count">
                {previewError
                    ? <span className="campaign-error-text">Audience preview failed: {previewError}</span>
                    : preview
                        ? <>
                            <strong>{preview.total}</strong> recipient{preview.total === 1 ? '' : 's'} match
                            {(preview.excluded?.suppressed || preview.excluded?.duplicates) ? (
                                <span className="campaign-muted">
                                    {' '}({preview.excluded.suppressed} suppressed, {preview.excluded.duplicates} duplicate{preview.excluded.duplicates === 1 ? '' : 's'} excluded)
                                </span>
                            ) : null}
                        </>
                        : 'Counting…'}
            </div>
        </section>
    );
};

// ---- Delivery stats + recipients ------------------------------------------

const DeliveryPanel = ({ campaignId, campaign, refreshToken, onCampaign }) => {
    const [stats, setStats] = useState(null);
    const [statusFilter, setStatusFilter] = useState('');
    const [page, setPage] = useState(1);
    const [recipients, setRecipients] = useState({ items: [], total: 0, size: 25 });

    // Sends run to completion server-side, but delivered/opened counts keep
    // moving afterwards as SendGrid webhooks land — poll while 'sending' and
    // refresh once more when the status flips.
    useEffect(() => {
        let cancelled = false;
        const load = () => {
            api.get(`admin/email/campaigns/${campaignId}`)
                .then((r) => {
                    if (cancelled) return;
                    setStats(r.stats);
                    onCampaign(r.campaign);
                })
                .catch(() => {});
        };
        load();
        if (campaign?.status !== 'sending') return () => { cancelled = true; };
        const timer = setInterval(load, 8000);
        return () => { cancelled = true; clearInterval(timer); };
    }, [campaignId, campaign?.status, refreshToken]);

    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams({ page: String(page), size: '25' });
        if (statusFilter) params.set('status', statusFilter);
        api.get(`admin/email/campaigns/${campaignId}/recipients?${params.toString()}`)
            .then((r) => { if (!cancelled) setRecipients(r); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [campaignId, statusFilter, page, refreshToken, campaign?.status]);

    const totals = stats?.totals || campaignTotals(campaign);
    const rates = stats?.rates || {};
    const pages = Math.max(1, Math.ceil((recipients.total || 0) / (recipients.size || 25)));

    const stat = (label, value, rate) => (
        <div className="campaign-stat">
            <span className="campaign-stat-value">
                {value}
                {rate !== undefined && rate !== null && <span className="campaign-stat-rate"> {rate}%</span>}
            </span>
            <span className="campaign-stat-label">{label}</span>
        </div>
    );

    return (
        <section className="campaign-card">
            <h2 className="campaign-card-title">Delivery</h2>
            <div className="campaign-stats-row">
                {stat('Recipients', totals.recipients)}
                {stat('Sent', totals.sent)}
                {stat('Delivered', totals.delivered, rates.delivered_rate)}
                {stat('Opened', totals.opened, rates.open_rate ?? openRate(totals))}
                {stat('Bounced', totals.bounced, rates.bounce_rate)}
                {stat('Failed', totals.failed)}
            </div>
            {campaign?.status === 'sending' && (
                <p className="campaign-hint">Sending in progress — figures refresh automatically.</p>
            )}

            <div className="campaign-recips-controls">
                <select
                    className="campaign-input"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                >
                    <option value="">All statuses</option>
                    {Object.entries(RECIPIENT_STATUS_META).map(([value, meta]) => (
                        <option key={value} value={value}>{meta.label}</option>
                    ))}
                </select>
                <span className="campaign-muted">{recipients.total} recipient{recipients.total === 1 ? '' : 's'}</span>
            </div>

            <div className="campaign-recips-table-wrap">
                <table className="campaign-recips-table">
                    <thead>
                        <tr>
                            <th>Email</th>
                            <th>Organisation</th>
                            <th>Status</th>
                            <th className="num">Opens</th>
                            <th>Detail</th>
                        </tr>
                    </thead>
                    <tbody>
                        {recipients.items.length === 0 ? (
                            <tr><td colSpan={5} className="campaign-recips-empty">No recipients in this view</td></tr>
                        ) : recipients.items.map((r) => {
                            const meta = RECIPIENT_STATUS_META[r.status] || { label: r.status, badge: 'ink' };
                            return (
                                <tr key={r.id}>
                                    <td>{r.email}</td>
                                    <td className="campaign-muted">{r.organization_name || '—'}</td>
                                    <td><span className={`badge ${meta.badge}`}>{meta.label}</span></td>
                                    <td className="num">{r.open_count || 0}</td>
                                    <td className="campaign-recips-detail">{r.error || r.skip_reason || ''}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {pages > 1 && (
                <div className="campaign-pager">
                    <button type="button" className="btn btn-secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
                    <span className="campaign-muted">Page {page} of {pages}</span>
                    <button type="button" className="btn btn-secondary" disabled={page >= pages} onClick={() => setPage(page + 1)}>Next</button>
                </div>
            )}
        </section>
    );
};

// ---- Main page ------------------------------------------------------------

const CampaignEditPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [campaign, setCampaign] = useState(null);
    const [loading, setLoading] = useState(!!id);
    const [name, setName] = useState('');
    const [subject, setSubject] = useState('');
    const [preheader, setPreheader] = useState('');
    const [htmlBody, setHtmlBody] = useState('');
    const [audience, setAudience] = useState(DEFAULT_AUDIENCE);

    const [mergeFields, setMergeFields] = useState([]);
    const [envInfo, setEnvInfo] = useState(null);
    const [preview, setPreview] = useState(null);
    const [testEmail, setTestEmail] = useState('');
    const [busy, setBusy] = useState(null); // 'save' | 'prepare' | 'test' | 'send' | 'cancel'
    const [notice, setNotice] = useState(null); // { kind: 'ok'|'err', text }
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [refreshToken, setRefreshToken] = useState(0);
    const bodyRef = useRef(null);

    useEffect(() => {
        api.get('admin/email/merge-fields')
            .then((r) => { setMergeFields(r.merge_fields || []); setEnvInfo(r); })
            .catch(() => {});
    }, []);

    useEffect(() => {
        if (!id) { setLoading(false); return; }
        setLoading(true);
        api.get(`admin/email/campaigns/${id}`)
            .then((r) => {
                applyCampaign(r.campaign);
            })
            .catch((e) => setNotice({ kind: 'err', text: `Failed to load campaign: ${errMsg(e)}` }))
            .finally(() => setLoading(false));
    }, [id]);

    const applyCampaign = (c) => {
        setCampaign(c);
        setName(c.name || '');
        setSubject(c.subject || '');
        setPreheader(c.preheader || '');
        setHtmlBody(c.html_body || '');
        setAudience(c.audience && Object.keys(c.audience).length ? c.audience : DEFAULT_AUDIENCE);
    };

    const isDraftEditable = !campaign || campaign.status === 'draft';
    const say = (kind, text) => setNotice({ kind, text });

    /* Templates: reusable subject/preheader/body starting points. Loaded for
     * the picker on new/draft campaigns; "Save as template" snapshots the
     * current editor content for the next campaign. */
    const [templates, setTemplates] = useState([]);
    useEffect(() => {
        api.get('admin/email/templates')
            .then((r) => setTemplates(r.templates || []))
            .catch(() => setTemplates([]));
    }, []);

    const applyTemplate = (templateId) => {
        const t = templates.find((x) => x.id === templateId);
        if (!t) return;
        if ((subject || htmlBody) && !window.confirm('Replace the current subject and body with this template?')) return;
        setSubject(t.subject || '');
        setPreheader(t.preheader || '');
        setHtmlBody(t.html_body || '');
        say('ok', `Template "${t.name}" loaded`);
    };

    const handleSaveTemplate = async () => {
        const suggested = name.trim() || subject.trim();
        const templateName = window.prompt('Template name:', suggested);
        if (!templateName) return;
        setBusy('template');
        setNotice(null);
        try {
            const r = await api.post('admin/email/templates', {
                name: templateName.trim(),
                subject: subject.trim() || '(no subject)',
                preheader: preheader.trim() || null,
                html_body: htmlBody,
            });
            setTemplates((list) => [r.template, ...list]);
            say('ok', `Saved as template "${r.template.name}"`);
        } catch (e) { say('err', errMsg(e)); }
        finally { setBusy(null); }
    };

    // Prepare/test act on the saved row, so dirty edits must land first.
    // Past draft the API refuses PATCH anyway — just return what we have.
    const ensureSaved = async () => {
        if (!name.trim()) throw new Error('Campaign name is required');
        if (!subject.trim()) throw new Error('Subject is required');
        if (campaign && campaign.status !== 'draft') return campaign;

        const payload = {
            name: name.trim(),
            subject: subject.trim(),
            preheader: preheader.trim() || null,
            html_body: htmlBody,
            audience,
        };
        if (!campaign) {
            const r = await api.post('admin/email/campaigns', payload);
            setCampaign(r.campaign);
            navigate(`/admin/emails/${r.campaign.id}`, { replace: true });
            return r.campaign;
        }
        const r = await api.patch(`admin/email/campaigns/${campaign.id}`, payload);
        setCampaign(r.campaign);
        return r.campaign;
    };

    const run = (key, fn) => async () => {
        setBusy(key);
        setNotice(null);
        try { await fn(); }
        catch (e) { say('err', errMsg(e)); }
        finally { setBusy(null); }
    };

    const handleSave = run('save', async () => {
        await ensureSaved();
        say('ok', 'Draft saved');
    });

    const handlePreview = run('preview', async () => {
        if (!htmlBody.trim()) throw new Error('Write some body HTML first');
        const r = await api.post('admin/email/preview', {
            html_body: htmlBody,
            subject: subject || '(no subject)',
            preheader: preheader || null,
            email: testEmail.trim() || undefined,
        });
        setPreview(r.preview);
    });

    const handlePrepare = run('prepare', async () => {
        const c = await ensureSaved();
        const r = await api.post(`admin/email/campaigns/${c.id}/prepare`);
        setCampaign(r.campaign);
        setRefreshToken((t) => t + 1);
        const n = campaignTotals(r.campaign).recipients;
        say('ok', `Audience prepared — ${n} recipient${n === 1 ? '' : 's'} resolved`);
    });

    const handleTest = run('test', async () => {
        const email = testEmail.trim();
        if (!email) throw new Error('Enter a test email address');
        const c = await ensureSaved();
        const r = await api.post(`admin/email/campaigns/${c.id}/test`, { to: email });
        say('ok', r.dry_run
            ? `Dry run — no email actually left (would go to ${email})`
            : `Test sent to ${email}`);
    });

    const handleSend = run('send', async () => {
        setConfirmOpen(false);
        // 202: the server dispatches in the background; status polling (below,
        // while 'sending') carries the campaign through to sent/failed.
        const r = await api.post(`admin/email/campaigns/${campaign.id}/send`, { confirm: true });
        setCampaign(r.campaign);
        setRefreshToken((t) => t + 1);
        say('ok', r.dry_run
            ? 'Dry run started — recipients are being processed, nothing actually leaves this server'
            : 'Sending started — this page will update as the send progresses');
    });

    const handleDelete = run('delete', async () => {
        if (!window.confirm('Delete this draft campaign? This cannot be undone.')) return;
        await api.delete(`admin/email/campaigns/${campaign.id}`);
        navigate('/admin/emails');
    });

    const handleCancel = run('cancel', async () => {
        const r = await api.post(`admin/email/campaigns/${campaign.id}/cancel`);
        setCampaign(r.campaign);
        say('ok', 'Campaign cancelled');
    });

    // Insert a {{token}} at the cursor so admins never have to remember the
    // merge syntax by hand.
    const insertMergeField = (token) => {
        const el = bodyRef.current;
        const snippet = `{{${token}}}`;
        if (!el) { setHtmlBody((v) => v + snippet); return; }
        const start = el.selectionStart ?? htmlBody.length;
        const end = el.selectionEnd ?? start;
        const next = htmlBody.slice(0, start) + snippet + htmlBody.slice(end);
        setHtmlBody(next);
        requestAnimationFrame(() => {
            el.focus();
            el.selectionStart = el.selectionEnd = start + snippet.length;
        });
    };

    if (loading) {
        return (
            <PageShell wrap={false}>
                <AdminNav />
                <div className="fs campaign-page"><div className="campaign-loading">Loading…</div></div>
            </PageShell>
        );
    }

    const meta = campaign ? (STATUS_META[campaign.status] || { label: campaign.status, badge: 'ink' }) : null;
    const totals = campaignTotals(campaign);

    return (
        <PageShell wrap={false}>
            <AdminNav />
            <div className="fs campaign-page">
                <button type="button" className="campaign-back" onClick={() => navigate('/admin/emails')}>
                    ← Back to campaigns
                </button>

                <div className="campaign-header">
                    <h1 className="campaign-title">{campaign ? campaign.name : 'New campaign'}</h1>
                    {meta && <span className={`badge ${meta.badge}`}>{meta.label}</span>}
                    {campaign && ['draft', 'ready'].includes(campaign.status) && (
                        <button
                            type="button"
                            className="btn btn-secondary campaign-cancel-btn"
                            disabled={busy !== null}
                            onClick={handleCancel}
                        >
                            Cancel campaign
                        </button>
                    )}
                    {campaign && campaign.status === 'draft' && (
                        <button
                            type="button"
                            className="btn btn-secondary campaign-cancel-btn"
                            disabled={busy !== null}
                            onClick={handleDelete}
                        >
                            Delete draft
                        </button>
                    )}
                </div>

                {envInfo?.dry_run && (
                    <div className="campaign-banner">
                        EMAIL_DRY_RUN is on — sends are simulated and nothing leaves this server.
                    </div>
                )}

                {notice && (
                    <div className={`campaign-notice ${notice.kind}`}>{notice.text}</div>
                )}

                {!isDraftEditable && campaign.status !== 'cancelled' && (
                    <div className="campaign-banner">
                        This campaign has been prepared or sent, so its content and audience are
                        locked — history stays stable. Create a new campaign to send something else.
                    </div>
                )}

                <section className="campaign-card">
                    <h2 className="campaign-card-title">Content</h2>
                    {isDraftEditable && templates.length > 0 && (
                        <label className="campaign-field">
                            <span className="campaign-label">Start from template</span>
                            <select
                                className="campaign-input"
                                value=""
                                onChange={(e) => e.target.value && applyTemplate(e.target.value)}
                            >
                                <option value="">Choose a template…</option>
                                {templates.map((t) => (
                                    <option key={t.id} value={t.id}>{t.name}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    <label className="campaign-field">
                        <span className="campaign-label">Campaign name (internal)</span>
                        <input
                            type="text"
                            className="campaign-input"
                            placeholder="e.g. September feature roundup"
                            value={name}
                            disabled={!isDraftEditable}
                            onChange={(e) => setName(e.target.value)}
                        />
                    </label>
                    <label className="campaign-field">
                        <span className="campaign-label">Subject line</span>
                        <input
                            type="text"
                            className="campaign-input"
                            placeholder="What shows in the inbox"
                            value={subject}
                            disabled={!isDraftEditable}
                            onChange={(e) => setSubject(e.target.value)}
                        />
                    </label>
                    <label className="campaign-field">
                        <span className="campaign-label">Preheader</span>
                        <input
                            type="text"
                            className="campaign-input"
                            placeholder="The snippet inbox clients show after the subject"
                            value={preheader}
                            disabled={!isDraftEditable}
                            onChange={(e) => setPreheader(e.target.value)}
                        />
                    </label>

                    <div className="campaign-field">
                        <div className="campaign-body-toolbar">
                            <span className="campaign-label">Body HTML</span>
                            <div className="campaign-merge-picker">
                                <span className="campaign-muted">Insert:</span>
                                {mergeFields.map((f) => (
                                    <button
                                        key={f.token}
                                        type="button"
                                        className="campaign-merge-btn"
                                        disabled={!isDraftEditable}
                                        onClick={() => insertMergeField(f.token)}
                                        title={`Inserts {{${f.token}}}`}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <textarea
                            ref={bodyRef}
                            className="campaign-input campaign-body"
                            placeholder="<p>Hi {{first_name}},</p> — the branded header and footer are added automatically."
                            value={htmlBody}
                            disabled={!isDraftEditable}
                            onChange={(e) => setHtmlBody(e.target.value)}
                            spellCheck={false}
                        />
                        <p className="campaign-hint">
                            Write only the inner content — the FreightSource header, footer and
                            unsubscribe link are wrapped around it at send time.
                            {htmlBody.trim() && (
                                <button
                                    type="button"
                                    className="campaign-merge-btn"
                                    style={{ marginLeft: 8 }}
                                    disabled={busy !== null}
                                    onClick={handleSaveTemplate}
                                >
                                    Save as template
                                </button>
                            )}
                        </p>
                    </div>
                </section>

                <AudienceSection audience={audience} onChange={setAudience} disabled={!isDraftEditable} />

                <section className="campaign-card">
                    <div className="campaign-card-head">
                        <h2 className="campaign-card-title">Preview</h2>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={busy !== null}
                            onClick={handlePreview}
                        >
                            {busy === 'preview' ? 'Rendering…' : 'Refresh preview'}
                        </button>
                    </div>
                    {preview ? (
                        <>
                            <p className="campaign-preview-subject">
                                <span className="campaign-muted">Subject:</span> <strong>{preview.subject}</strong>
                            </p>
                            <iframe
                                title="Wrapped email preview"
                                className="campaign-preview-frame"
                                srcDoc={preview.html}
                                sandbox=""
                            />
                            <p className="campaign-hint">
                                Exactly the HTML a real send produces, branded wrapper included.
                                {preview.matchedRealUser
                                    ? ` Merge fields filled from ${testEmail.trim()}'s real account.`
                                    : ' Merge fields use sample values — put a real user\'s address in the test box to see theirs.'}
                            </p>
                        </>
                    ) : (
                        <p className="campaign-hint">Click "Refresh preview" to render the wrapped email.</p>
                    )}
                </section>

                <section className="campaign-card">
                    <h2 className="campaign-card-title">Test &amp; send</h2>
                    <p className="campaign-hint">
                        Test sends only reach addresses on the server's EMAIL_TEST_ALLOWLIST
                        {envInfo && !envInfo.test_send_live && ', and this instance is in dry-run so even those are simulated'}.
                    </p>
                    <div className="campaign-test-row">
                        <input
                            type="email"
                            className="campaign-input"
                            placeholder="Send test to…"
                            value={testEmail}
                            onChange={(e) => setTestEmail(e.target.value)}
                        />
                        <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={handleTest}>
                            {busy === 'test' ? 'Sending…' : 'Send test'}
                        </button>
                    </div>

                    <div className="campaign-action-rail">
                        {isDraftEditable && (
                            <button type="button" className="btn btn-secondary" disabled={busy !== null} onClick={handleSave}>
                                {busy === 'save' ? 'Saving…' : 'Save draft'}
                            </button>
                        )}
                        <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={busy !== null || (campaign && !['draft', 'ready'].includes(campaign.status))}
                            onClick={handlePrepare}
                        >
                            {busy === 'prepare'
                                ? 'Preparing…'
                                : campaign?.status === 'ready' ? 'Re-prepare audience' : 'Prepare audience'}
                        </button>
                        {campaign?.status === 'ready' && (
                            <span className="campaign-muted">
                                {totals.recipients} recipient{totals.recipients === 1 ? '' : 's'} resolved
                            </span>
                        )}
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={busy !== null || !campaign || campaign.status !== 'ready'}
                            onClick={() => setConfirmOpen(true)}
                        >
                            {busy === 'send' ? 'Sending…' : 'Send'}
                        </button>
                    </div>
                    {(!campaign || campaign.status === 'draft') && (
                        <p className="campaign-hint">
                            Prepare the audience before sending — it freezes the current filters
                            into a fixed recipient list.
                        </p>
                    )}
                </section>

                {campaign && campaign.status !== 'draft' && campaign.status !== 'cancelled' && (
                    <DeliveryPanel
                        campaignId={campaign.id}
                        campaign={campaign}
                        refreshToken={refreshToken}
                        onCampaign={setCampaign}
                    />
                )}

                {confirmOpen && (
                    <div className="campaign-modal-overlay" role="dialog" aria-modal="true">
                        <div className="campaign-modal">
                            <h3>Send this campaign now?</h3>
                            <p>
                                "{campaign?.name}" will be emailed to{' '}
                                <strong>{totals.recipients} real recipient{totals.recipients === 1 ? '' : 's'}</strong>.
                                This cannot be undone.
                            </p>
                            <div className="campaign-modal-actions">
                                <button type="button" className="btn btn-secondary" onClick={() => setConfirmOpen(false)}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary" disabled={busy !== null} onClick={handleSend}>
                                    Send now
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </PageShell>
    );
};

export default CampaignEditPage;
